import React, { useEffect, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getAvailableLanguages, getCurrentLanguage, setAppLang } from '@/lib/langManager'
import CachedImage from '@/components/ui/CachedImage'
import { Button } from '@/components/admin/ds/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/admin/ds/dropdown-menu'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/admin/ds/sheet'
import { cn } from '@/lib/admin-ds'
import {
  BookIcon,
  CpuIcon,
  GroupIcon,
  HomeSimpleIcon,
  LogOutIcon,
  MenuIcon,
  MessageTextIcon,
  MicrophoneIcon,
  MoreHorizIcon,
  NavArrowDownIcon,
  PageIcon,
  WarningTriangleIcon,
} from '@/components/admin/icons'

type NavItem = {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  match?: (pathname: string) => boolean
  /** Soft accent for icon + active chip so nav isn't grey-on-white. */
  tone?: 'violet' | 'sky' | 'emerald' | 'amber' | 'rose' | 'indigo' | 'teal'
}

const TONE: Record<
  NonNullable<NavItem['tone']>,
  { idle: string; active: string; icon: string }
> = {
  violet: {
    idle: 'text-violet-700 hover:bg-violet-100/80 hover:text-violet-900',
    active: 'bg-violet-600 text-white shadow-sm shadow-violet-600/25 hover:bg-violet-600 hover:text-white',
    icon: 'text-violet-600',
  },
  sky: {
    idle: 'text-sky-700 hover:bg-sky-100/80 hover:text-sky-900',
    active: 'bg-sky-600 text-white shadow-sm shadow-sky-600/25 hover:bg-sky-600 hover:text-white',
    icon: 'text-sky-600',
  },
  emerald: {
    idle: 'text-emerald-700 hover:bg-emerald-100/80 hover:text-emerald-900',
    active: 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/25 hover:bg-emerald-600 hover:text-white',
    icon: 'text-emerald-600',
  },
  amber: {
    idle: 'text-amber-800 hover:bg-amber-100/80 hover:text-amber-950',
    active: 'bg-amber-500 text-white shadow-sm shadow-amber-500/25 hover:bg-amber-500 hover:text-white',
    icon: 'text-amber-600',
  },
  rose: {
    idle: 'text-rose-700 hover:bg-rose-100/80 hover:text-rose-900',
    active: 'bg-rose-600 text-white shadow-sm shadow-rose-600/25 hover:bg-rose-600 hover:text-white',
    icon: 'text-rose-600',
  },
  indigo: {
    idle: 'text-indigo-700 hover:bg-indigo-100/80 hover:text-indigo-900',
    active: 'bg-indigo-600 text-white shadow-sm shadow-indigo-600/25 hover:bg-indigo-600 hover:text-white',
    icon: 'text-indigo-600',
  },
  teal: {
    idle: 'text-teal-700 hover:bg-teal-100/80 hover:text-teal-900',
    active: 'bg-teal-600 text-white shadow-sm shadow-teal-600/25 hover:bg-teal-600 hover:text-white',
    icon: 'text-teal-600',
  },
}

const PRIMARY_NAV: NavItem[] = [
  {
    to: '/admin/dashboard',
    label: 'Dashboard',
    icon: HomeSimpleIcon,
    tone: 'violet',
    match: (p) => p === '/admin/dashboard' || p === '/admin',
  },
  {
    to: '/admin/students',
    label: 'Students',
    icon: GroupIcon,
    tone: 'sky',
    match: (p) => p.startsWith('/admin/students'),
  },
  {
    to: '/admin/content',
    label: 'Content',
    icon: BookIcon,
    tone: 'emerald',
    match: (p) => p.startsWith('/admin/content'),
  },
  {
    to: '/admin/transcripts',
    label: 'Transcripts',
    icon: MessageTextIcon,
    tone: 'indigo',
  },
  {
    to: '/admin/logs',
    label: 'Logs',
    icon: PageIcon,
    tone: 'amber',
    match: (p) => p === '/admin/logs' || p === '/admin/speaking-test',
  },
  {
    to: '/admin/robot-detect',
    label: 'Robot Detect',
    icon: CpuIcon,
    tone: 'teal',
    match: (p) => p.startsWith('/admin/robot-detect'),
  },
  {
    to: '/admin/punish',
    label: 'Punish',
    icon: WarningTriangleIcon,
    tone: 'rose',
  },
]

const MORE_NAV: NavItem[] = [
  {
    to: '/admin/reading-detector-test',
    label: 'Reading Detector',
    icon: PageIcon,
  },
  {
    to: '/admin/improve-transcript-test',
    label: 'Improve Transcript',
    icon: MessageTextIcon,
  },
  {
    to: '/admin/robot-detect/check-tts',
    label: 'Check TTS',
    icon: MicrophoneIcon,
  },
  {
    to: '/admin/students/test-lesson',
    label: 'Test Lesson',
    icon: BookIcon,
  },
]

function isActive(item: NavItem, pathname: string) {
  if (item.match) return item.match(pathname)
  return pathname === item.to || pathname.startsWith(item.to + '/')
}

function NavLinkButton({
  item,
  pathname,
  onNavigate,
}: {
  item: NavItem
  pathname: string
  onNavigate?: () => void
}) {
  const active = isActive(item, pathname)
  const Icon = item.icon
  const tone = TONE[item.tone || 'violet']
  return (
    <NavLink
      to={item.to}
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
      className={cn(
        'inline-flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors',
        active ? tone.active : tone.idle,
      )}
    >
      <Icon
        className={cn('size-4 shrink-0', active ? 'text-white' : tone.icon)}
        aria-hidden
      />
      <span className="whitespace-nowrap">{item.label}</span>
    </NavLink>
  )
}

export default function AdminHeader() {
  const { t } = useTranslation('admin')
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [mounted, setMounted] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const adminEmail = typeof window !== 'undefined' ? localStorage.getItem('adminEmail') : null

  useEffect(() => {
    setMounted(true)
  }, [])

  const currentLang = mounted
    ? getCurrentLanguage()
    : getAvailableLanguages().find((lang) => lang.code === 'en')
  const languages = getAvailableLanguages()
  const moreActive = MORE_NAV.some((item) => isActive(item, pathname))

  const handleLogout = async () => {
    try {
      const { apiClient } = await import('@/lib/api')
      await apiClient.logout()
    } catch {
      // Continue logout even if API fails
    }
    const cookieConsentData = localStorage.getItem('cookie_consent_data')
    localStorage.clear()
    if (cookieConsentData) {
      localStorage.setItem('cookie_consent_data', cookieConsentData)
    }
    window.location.href = '/'
  }

  const handleLanguageChange = (langCode: 'en' | 'th') => {
    setAppLang(langCode)
  }

  return (
    <header className="sticky top-0 z-40 overflow-visible border-b border-primary/20 bg-gradient-to-r from-primary/[0.12] via-background/90 to-sky-100/50 backdrop-blur-md supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex max-w-[1400px] items-center gap-3 overflow-visible px-4 py-3 sm:gap-6 sm:px-6">
        <button
          type="button"
          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-primary/25 bg-white/90 px-2.5 text-primary hover:bg-primary/10"
          aria-label="Open navigation menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(true)}
        >
          <MenuIcon className="size-5 shrink-0" aria-hidden />
          <span className="pr-0.5 text-sm font-medium">Menu</span>
        </button>

        <Link
          to="/admin/dashboard"
          className="flex shrink-0 items-center gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <CachedImage src="/logo.webp" alt="TutorCat" className="h-9 w-9 object-contain" />
          <div className="hidden min-[420px]:block">
            <div className="text-sm font-semibold text-foreground leading-tight">TutorCat Admin</div>
            <div className="text-xs text-primary/80 leading-tight font-medium">Control panel</div>
          </div>
        </Link>

        <nav aria-label="Primary" className="hidden xl:flex flex-1 items-center gap-1 min-w-0">
          {PRIMARY_NAV.map((item) => (
            <NavLinkButton key={item.to} item={item} pathname={pathname} />
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                'inline-flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors',
                moreActive
                  ? 'bg-violet-600 text-white shadow-sm shadow-violet-600/25'
                  : 'text-violet-700 hover:bg-violet-100/80 hover:text-violet-900',
              )}
            >
              <MoreHorizIcon
                className={cn('size-4', moreActive ? 'text-white' : 'text-violet-600')}
                aria-hidden
              />
              More
              <NavArrowDownIcon className="size-3.5 opacity-70" aria-hidden />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {MORE_NAV.map((item) => {
                const Icon = item.icon
                const active = isActive(item, pathname)
                return (
                  <DropdownMenuItem
                    key={item.to}
                    onClick={() => navigate(item.to)}
                    className="gap-2"
                  >
                    <Icon className="size-4" aria-hidden />
                    <span className={active ? 'font-medium' : undefined}>{item.label}</span>
                  </DropdownMenuItem>
                )
              })}
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs font-medium text-neutral-500">Language</div>
              {languages.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code as 'en' | 'th')}
                  className="gap-2"
                >
                  <CachedImage
                    src={lang.flag}
                    alt=""
                    className="h-5 w-5 rounded-full object-contain"
                  />
                  <span className="text-sm text-neutral-900">{lang.nativeName}</span>
                  {currentLang?.code === lang.code ? (
                    <span className="ml-auto text-xs font-medium text-violet-600">Active</span>
                  ) : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {adminEmail ? (
            <span className="hidden lg:inline text-sm text-muted-foreground max-w-[180px] truncate">
              {adminEmail}
            </span>
          ) : null}

          <Button variant="secondary" size="sm" className="h-10 gap-2" onClick={handleLogout}>
            <LogOutIcon className="size-4" aria-hidden />
            <span className="hidden sm:inline">{t('logout', 'Logout')}</span>
          </Button>
        </div>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[300px] sm:w-[340px]">
          <SheetHeader>
            <SheetTitle>Admin navigation</SheetTitle>
          </SheetHeader>
          <nav aria-label="Mobile" className="mt-6 flex flex-col gap-1">
            {[...PRIMARY_NAV, ...MORE_NAV].map((item) => {
              const Icon = item.icon
              const active = isActive(item, pathname)
              const tone = item.tone ? TONE[item.tone] : TONE.violet
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    'inline-flex h-11 items-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors',
                    active ? tone.active : tone.idle,
                  )}
                >
                  <Icon
                    className={cn('size-4 shrink-0', active ? 'text-white' : tone.icon)}
                    aria-hidden
                  />
                  <span>{item.label}</span>
                </NavLink>
              )
            })}
            <div className="my-3 border-t border-neutral-200 pt-3">
              <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wide text-neutral-500">
                Language
              </p>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    handleLanguageChange(lang.code as 'en' | 'th')
                    setMobileOpen(false)
                  }}
                  className={cn(
                    'inline-flex h-11 w-full items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors',
                    currentLang?.code === lang.code
                      ? 'bg-violet-100 text-violet-900'
                      : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900',
                  )}
                >
                  <CachedImage
                    src={lang.flag}
                    alt=""
                    className="h-5 w-5 rounded-full object-contain"
                  />
                  <span>{lang.nativeName}</span>
                  {currentLang?.code === lang.code ? (
                    <span className="ml-auto text-xs font-medium">Active</span>
                  ) : null}
                </button>
              ))}
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  )
}
